import { Mp3Encoder } from '@breezystack/lamejs';
import type { TMetaAudioUploadContentType } from './audioRecorder';

const MP3_BITRATE_KBPS = 64;
const MP3_SAMPLE_BLOCK_SIZE = 1152;

const floatTo16BitPCM = (input: Float32Array): Int16Array => {
    const output = new Int16Array(input.length);

    for (let index = 0; index < input.length; index += 1) {
        const sample = Math.max(-1, Math.min(1, input[index]));
        output[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    }

    return output;
};

const mixToMono = (audioBuffer: AudioBuffer): Float32Array => {
    if (audioBuffer.numberOfChannels === 1) {
        return audioBuffer.getChannelData(0);
    }

    const left = audioBuffer.getChannelData(0);
    const right = audioBuffer.getChannelData(1);
    const mono = new Float32Array(left.length);

    for (let index = 0; index < left.length; index += 1) {
        mono[index] = (left[index] + right[index]) / 2;
    }

    return mono;
};

export const transcodeAudioBlobToMp3 = async (blob: Blob): Promise<File> => {
    const audioContext = new AudioContext();

    try {
        const arrayBuffer = await blob.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(
            arrayBuffer.slice(0)
        );
        const samples = floatTo16BitPCM(mixToMono(audioBuffer));
        const mp3Encoder = new Mp3Encoder(
            1,
            audioBuffer.sampleRate,
            MP3_BITRATE_KBPS
        );
        const mp3Chunks: Uint8Array[] = [];

        for (
            let offset = 0;
            offset < samples.length;
            offset += MP3_SAMPLE_BLOCK_SIZE
        ) {
            const chunk = samples.subarray(
                offset,
                offset + MP3_SAMPLE_BLOCK_SIZE
            );
            const encoded = mp3Encoder.encodeBuffer(chunk);

            if (encoded.length > 0) {
                mp3Chunks.push(encoded);
            }
        }

        const flushed = mp3Encoder.flush();

        if (flushed.length > 0) {
            mp3Chunks.push(flushed);
        }

        if (!mp3Chunks.length) {
            throw new Error('Errors.UnsupportedAudioFormat');
        }

        const mp3Blob = new Blob(mp3Chunks as BlobPart[], { type: 'audio/mpeg' });

        return new File([mp3Blob], `voice-message-${Date.now()}.mp3`, {
            type: 'audio/mpeg',
        });
    } finally {
        if (audioContext.state !== 'closed') {
            await audioContext.close();
        }
    }
};

/**
 * All recorded audio is transcoded to MP3 before upload for Meta/WhatsApp compatibility.
 */
export const normalizeAudioForMetaUpload = async (
    file: File
): Promise<File> => {
    if (!file.type.toLowerCase().startsWith('audio/')) {
        return file;
    }

    if (file.type === 'audio/mpeg') {
        return file;
    }

    return transcodeAudioBlobToMp3(file);
};

export const getAudioUploadContentType = (
    file: File
): TMetaAudioUploadContentType => {
    if (file.type === 'audio/mpeg') {
        return 'audio/mpeg';
    }

    throw new Error('Errors.UnsupportedAudioFormat');
};
